import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Label, type LabelProps } from "../Label"
import { cn } from "../../../lib/utils"

const inputGroupVariants = cva("flex flex-col", {
  variants: {
    size: {
      sm: "gap-1.5",
      md: "gap-2",
      lg: "gap-3",
    },
  },
  defaultVariants: {
    size: "md",
  },
})

const helperTextVariants = cva("font-black tracking-tight", {
  variants: {
    size: {
      sm: "text-[10px]",
      md: "text-xs",
      lg: "text-sm",
    },
  },
  defaultVariants: {
    size: "md",
  },
})

export interface InputGroupProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof inputGroupVariants> {
  /**
   * Label text for the input
   */
  label?: string
  /**
   * Props to pass to the Label component
   */
  labelProps?: Omit<LabelProps, "children">
  /**
   * Helper text displayed below the input
   */
  helperText?: string
  /**
   * Error message displayed below the input (takes precedence over helperText)
   */
  error?: string
  /**
   * Info message displayed below the input
   */
  info?: string
  /**
   * If true, displays a required indicator on the label
   */
  required?: boolean
  /**
   * ID for the input element (used for label's htmlFor)
   */
  htmlFor?: string
  /**
   * If true, the group is disabled
   */
  disabled?: boolean
}

function InputGroup({
  className,
  size,
  label,
  labelProps,
  helperText,
  error,
  info,
  required,
  htmlFor,
  disabled,
  children,
  ...props
}: InputGroupProps) {
  const hasError = Boolean(error)

  return (
    <div
      data-slot="input-group"
      data-size={size}
      data-disabled={disabled || undefined}
      data-error={hasError || undefined}
      className={cn(inputGroupVariants({ size }), "group text-left", className)}
      {...props}
    >
      {label && (
        <Label
          htmlFor={htmlFor}
          size={size}
          required={required}
          {...labelProps}
        >
          {label}
        </Label>
      )}
      {children}
      {(error || helperText || info) && (
        <div
          data-slot="input-group-message"
          className={cn(
            helperTextVariants({ size }),
            hasError ? "text-destructive font-semibold" : "text-muted-foreground"
          )}
        >
          {error || helperText}
          {info && !error && (
            <span className="text-muted-foreground ml-1">({info})</span>
          )}
        </div>
      )}
    </div>
  )
}

InputGroup.displayName = "InputGroup"

export { InputGroup }
